<template>
  <div class="text-input"
       :class="{'text-right':textAlign === 'right',
                'read-only':!!readOnlyStyle}">
    <label v-show="!!label"
           :class="{translateLabel}"
           @click="$refs['input'].focus()">
      {{ label }}
    </label>
    <input ref="input"
           :value="value"
           v-bind="attrs"
           v-on="listeners">
    <slot name="errors"></slot>
    <div v-if="!!$scopedSlots.icon" class="flex items-center absolute right-4 inset-y-0 m-auto">
      <slot name="icon"></slot>
    </div>
  </div>
</template>

<script>

export default {
  name: "BaseInput",
  inheritAttrs: false,
  data() {
    return {
      translateLabel: false,
      passwordRevealed: false,
      debounceTimeout: null
    }
  },
  props: {
    label: {
      type: String,
      default: null
    },
    textAlign: {
      type: String,
      default: 'left'
    },
    readOnlyStyle: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Number]
    },
    debounce: {
      type: Number
    }
  },
  computed: {
    listeners() {
      return {
        ...this.$listeners,
        focusin: () => {
          if (Object.keys(this.$listeners).includes('focusin')) {
            this.$listeners['focusin']()
          }
          this.translateLabel = true
        },
        focusout: event => {
          if (Object.keys(this.$listeners).includes('focusout')) {
            this.$listeners['focusout']()
          }
          if (!event.target.value) {
            this.translateLabel = false
          }
        },
        input: (event) => this.updateValue(event.target.value)
      }
    },
    attrs() {
      return {
        ...this.$attrs,
        readonly: this.readOnlyStyle
      }
    }
  },
  methods: {
    updateValue(value) {
      if (this.debounce) {
        clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          this.$emit('input', value)
        }, this.debounce)
      } else {
        this.$emit('input', value)
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.translateLabel = val === 0 ? true : !!val;
      }
    }
  }
}
</script>

<style lang="scss">
.text-input {
  position: relative;

  input {
    @apply bg-block border border-border rounded shadow-input
    px-5-6 py-4-5 w-full
    text-md text-text-secondary outline-none;
    transition: all .2s;

    &:focus {
      @apply border-border-hover;
    }

    &:disabled {
      opacity: .6;
    }
  }

  label {
    @apply text-md text-text-ternary px-5-6 top-0 pt-4-5 transform absolute z-10 cursor-text;
    transition: all .2s;

    &:before {
      @apply bg-block left-2;
      content: '';
      position: absolute;
      right: 0;
      top: 35%;
      width: calc(100% - 1rem);
      height: 9px;
      filter: blur(3px);
      z-index: -1;
    }

    &.translateLabel {
      @apply text-xs text-text-label pt-0 top-0 -translate-y-1/2;
    }
  }

  &:hover {
    input {
      @apply border-border-hover;
    }
  }

  .error {
    display: none;
  }

  &.read-only {
    input {
      @apply bg-transparent border-t-0 border-l-0 border-r-0 border-b rounded-none shadow-none;
    }
  }

  &.text-right {
    label, .error {
      @apply right-0;
    }

    label:before {
      @apply right-2;
    }

    input {
      @apply text-right;
    }
  }

  &.--error {
    input {
      animation-name: shakeError;
      animation-fill-mode: forwards;
      animation-duration: 0.6s;
      animation-timing-function: ease-in-out;
      @apply border-red;
    }

    label {
      @apply text-xs text-red pt-0 pr-6 top-0 -translate-y-1/2 ;
    }

    .error {
      @apply block text-red text-xs absolute bottom-0 transform translate-y-full px-5-6;
    }
  }
}

@keyframes shakeError {
  0% {
    transform: translateX(0px);
  }
  15% {
    transform: translateX(0.375rem);
  }
  30% {
    transform: translateX(-0.375rem);
  }
  45% {
    transform: translateX(0.375rem);
  }
  60% {
    transform: translateX(-0.375rem);
  }
  75% {
    transform: translateX(0.375rem);
  }
  90% {
    transform: translateX(-0.375rem);
  }
  100% {
    transform: translateX(0px);
  }
}
</style>
